import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Button, Card, CardContent, Chip, Collapse, Divider, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import DataTable from '../shared/DataTable';
import CustomerAutocomplete from '../shared/CustomerAutocomplete';
import DecimalField from '../shared/DecimalField';
import { formatPrice, formatPriceWithCurrency } from '../../utils/vat';
import { codeFont, numFont, plateFont } from '../../utils/fonts';
import { customerLabel } from '../../utils/customer';

export function formatCabasDate(value) {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return String(value);
  return `${parseInt(day, 10)}.${parseInt(month, 10)}.${year}`;
}

function formatQuantity(value) {
  const num = parseFloat(value) || 0;
  return Number.isInteger(num) ? String(num) : formatPrice(num).replace(/0$/, '');
}

const ACTION_COLOR = { existing: 'default', create: 'info', none: 'default' };

function ActionChip({ resolution, chosen = false }) {
  const { t } = useTranslation();
  if (!resolution) return null;
  let label = t('cabas.preview.actionNone');
  if (chosen) label = t('cabas.preview.actionChosen');
  else if (resolution.kept) label = t('cabas.preview.actionKept');
  else if (resolution.action === 'existing') label = t('cabas.preview.actionExisting');
  else if (resolution.action === 'create') label = t('cabas.preview.actionCreate');
  return (
    <Chip
      size="small"
      variant="outlined"
      color={chosen ? 'default' : ACTION_COLOR[resolution.action] || 'default'}
      label={label}
      sx={{ ml: 'auto' }}
    />
  );
}

function InfoCard({ icon, title, resolution, chosen, children, testId }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: '10px', minWidth: 0 }} data-testid={testId}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {icon}
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {title}
          </Typography>
          <ActionChip resolution={resolution} chosen={chosen} />
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function Line({ children, secondary = false, sx, ...rest }) {
  return (
    <Typography
      variant="body2"
      color={secondary ? 'text.secondary' : 'text.primary'}
      sx={{ overflowWrap: 'anywhere', ...sx }}
      {...rest}
    >
      {children}
    </Typography>
  );
}

function ModeBanner({ preview }) {
  const { t } = useTranslation();
  const workNumber = preview.maintenance?.work_number;
  const errors = preview.issues.filter((issue) => issue.level === 'error');

  if (preview.mode === 'up_to_date') {
    return (
      <Alert severity="success" sx={{ mb: 2 }} data-testid="cabas-mode">
        {workNumber ? t('cabas.preview.modeUpToDate', { number: workNumber }) : t('cabas.preview.modeUpToDateHidden')}
      </Alert>
    );
  }
  if (preview.mode === 'linked_elsewhere') {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} data-testid="cabas-mode">
        {workNumber
          ? t('cabas.preview.modeLinkedElsewhere', { number: workNumber })
          : t('cabas.preview.modeLinkedElsewhereHidden')}
      </Alert>
    );
  }
  if (preview.blocked) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} data-testid="cabas-mode">
        <AlertTitle>{t('cabas.preview.blockedTitle')}</AlertTitle>
        {errors.map((issue) => (
          <Box key={issue.code + issue.message}>{issue.message}</Box>
        ))}
      </Alert>
    );
  }
  const text = {
    create: t('cabas.preview.modeCreate'),
    attach: t('cabas.preview.modeAttach'),
    update: t('cabas.preview.modeUpdate', {
      date: formatCabasDate(preview.previous?.imported_at),
    }),
  }[preview.mode];
  return (
    <Alert severity="info" sx={{ mb: 2 }} data-testid="cabas-mode">
      {text}
    </Alert>
  );
}

const CONTACT_TYPE_KEYS = {
  HolderContact: 'cabas.preview.contactHolder',
  UserContact: 'cabas.preview.contactUser',
  OwnerContact: 'cabas.preview.contactOwner',
};


/**
 * insurerCustomer / onInsurerCustomerChange: for an insurer code the workshop has not
 * mapped yet, the user may pick the existing customer the invoices go to instead of
 * creating a new one. Mapped codes are changed in Settings → Integrations.
 *
 * marginPrices / onMarginPriceChange(key, value): purchase prices of the margin-VAT used
 * parts (preview.margin_parts). CABAS does not send them and the margin VAT needs them.
 */
export default function CabasEstimatePreview({
  preview,
  insurerCustomer = null,
  onInsurerCustomerChange,
  marginPrices = {},
  onMarginPriceChange,
}) {
  const { t } = useTranslation();
  const [notesOpen, setNotesOpen] = useState(false);
  if (!preview) return null;
  const { vehicle, customer, insurer, owner, header, totals } = preview;
  const warnings = preview.issues.filter((issue) => issue.level === 'warning');
  const infos = preview.issues.filter((issue) => issue.level === 'info');
  const canPickInsurer =
    Boolean(onInsurerCustomerChange) && preview.can_import && insurer && !insurer.linked && Boolean(insurer.cabas_name || insurer.code);
  const insurerName = insurerCustomer ? customerLabel(insurerCustomer) : insurer?.name;

  return (
    <Box data-testid="cabas-preview">
      <ModeBanner preview={preview} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <InfoCard
          icon={<DirectionsCarIcon fontSize="small" color="primary" />}
          title={t('cabas.preview.vehicle')}
          resolution={vehicle}
          testId="cabas-preview-vehicle"
        >
          <Line sx={{ ...plateFont, fontWeight: 700 }}>{vehicle?.registration_number || '-'}</Line>
          {(vehicle?.make || vehicle?.model) && (
            <Line secondary>{[vehicle.make, vehicle.model, vehicle.year_model].filter(Boolean).join(' ')}</Line>
          )}
          {vehicle?.vin && <Line secondary sx={{ ...numFont, fontSize: '12px' }}>{vehicle.vin}</Line>}
        </InfoCard>

        <InfoCard
          icon={<PersonIcon fontSize="small" color="primary" />}
          title={t('cabas.preview.customer')}
          resolution={customer}
          testId="cabas-preview-customer"
        >
          {customer?.name ? (
            <>
              <Line sx={{ fontWeight: 500 }}>{customer.name}</Line>
              {CONTACT_TYPE_KEYS[customer.source_contact_type] && !customer.kept && (
                <Line secondary>{t(CONTACT_TYPE_KEYS[customer.source_contact_type])}</Line>
              )}
              {customer.phone && <Line secondary>{customer.phone}</Line>}
              {customer.email && <Line secondary>{customer.email}</Line>}
              {customer.address && <Line secondary>{customer.address}</Line>}
            </>
          ) : (
            <Line secondary>{t('cabas.preview.noCustomer')}</Line>
          )}
          {owner?.name && (
            <Line secondary sx={{ mt: 1 }}>
              {t('cabas.preview.owner')}: {owner.name}
            </Line>
          )}
        </InfoCard>

        <InfoCard
          icon={<SecurityIcon fontSize="small" color="primary" />}
          title={t('cabas.preview.insurer')}
          resolution={insurer}
          chosen={Boolean(insurerCustomer)}
          testId="cabas-preview-insurer"
        >
          {insurerName ? (
            <>
              <Line sx={{ fontWeight: 500 }}>{insurerName}</Line>
              <Line secondary>{t('cabas.preview.insurerBilling')}</Line>
            </>
          ) : (
            <Line secondary>{t('cabas.preview.noInsurer')}</Line>
          )}
          {canPickInsurer && (
            <Box sx={{ my: 1 }} data-testid="cabas-insurer-picker">
              <CustomerAutocomplete
                value={insurerCustomer}
                onChange={onInsurerCustomerChange}
                label={t('cabas.preview.insurerPick')}
                helperText={t('cabas.preview.insurerPickHelp')}
              />
            </Box>
          )}
          {header.claim_number && (
            <Line secondary>
              {t('cabas.preview.claimNumber')}: <Box component="span" sx={codeFont}>{header.claim_number}</Box>
            </Line>
          )}
          {header.damage_date && (
            <Line secondary>
              {t('cabas.preview.damageDate')}: {formatCabasDate(header.damage_date)}
              {header.damage_type ? ` (${t(`damageType.${header.damage_type}`)})` : ''}
            </Line>
          )}
          <Line secondary>
            {t('cabas.preview.deductible')}:{' '}
            {header.deductible_amount != null ? formatPriceWithCurrency(header.deductible_amount) : t('cabas.preview.noDeductible')}
          </Line>
          {header.vat_deductible != null && (
            <Line secondary>
              {header.vat_deductible ? t('cabas.preview.vatDeductible') : t('cabas.preview.notVatDeductible')}
            </Line>
          )}
          {header.vat_method === 'after_deductible' && (
            <Line secondary data-testid="cabas-preview-vat-method">{t('cabas.preview.vatAfterDeductible')}</Line>
          )}
          {preview.vat_invoice_customer?.name && (
            <Line secondary>{t('cabas.preview.vatInvoiceTo', { name: preview.vat_invoice_customer.name })}</Line>
          )}
        </InfoCard>
      </Box>

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="cabas-warnings">
          {warnings.map((issue) => (
            <Box key={issue.code + issue.message}>{issue.message}</Box>
          ))}
        </Alert>
      )}

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('cabas.preview.lines')}
      </Typography>
      <DataTable
        columns={[
          {
            key: 'kind',
            label: '',
            width: '1%',
            noWrap: true,
            render: (line) => (
              <Chip
                size="small"
                variant="outlined"
                color={line.kind === 'job' ? 'primary' : 'default'}
                label={line.kind === 'job' ? t('cabas.preview.kindJob') : t('cabas.preview.kindPart')}
                sx={{ fontSize: '11px', height: 22 }}
              />
            ),
          },
          {
            key: 'name',
            label: t('cabas.preview.lineName'),
            maxWidth: 320,
            render: (line) => (
              <Box>
                <Typography variant="body2">{line.name}</Typography>
                {line.description && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {line.description}
                  </Typography>
                )}
                {line.part_number && (
                  <Typography variant="caption" color="text.secondary" sx={codeFont}>
                    {line.part_number}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'quantity',
            label: t('cabas.preview.quantity'),
            align: 'right',
            noWrap: true,
            render: (line) => (
              <Box component="span" sx={numFont}>
                {formatQuantity(line.quantity)}
                {line.hourly ? ' h' : ''}
              </Box>
            ),
          },
          {
            key: 'unit',
            label: t('cabas.preview.unitPrice'),
            align: 'right',
            noWrap: true,
            hideBelow: 'sm',
            render: (line) => <Box component="span" sx={numFont}>{formatPrice(line.unit_price)}</Box>,
          },
          {
            key: 'discount',
            label: t('cabas.preview.discount'),
            align: 'right',
            noWrap: true,
            hideBelow: 'sm',
            render: (line) =>
              parseFloat(line.discount_percent) ? (
                <Box component="span" sx={numFont}>{formatPrice(line.discount_percent)} %</Box>
              ) : (
                ''
              ),
          },
          {
            key: 'total',
            label: t('cabas.preview.totalExclVat'),
            align: 'right',
            noWrap: true,
            render: (line) => <Box component="span" sx={{ ...numFont, fontWeight: 500 }}>{formatPrice(line.total)}</Box>,
          },
        ]}
        data={preview.lines.map((line, index) => ({ ...line, id: `${line.key}-${index}` }))}
        alwaysVisible
        horizontalScroll
        emptyTitle={t('cabas.preview.noLines')}
      />

      {preview.margin_parts?.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="cabas-margin-parts">
          <AlertTitle>{t('cabas.preview.marginParts')}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('cabas.preview.marginPartsHelp')}
          </Typography>
          <Stack spacing={1.5}>
            {preview.margin_parts.map((part) => (
              <Box
                key={part.key}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {part.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={numFont}>
                    {formatQuantity(part.quantity)} × {formatPriceWithCurrency(part.amount)}
                  </Typography>
                </Box>
                <DecimalField
                  size="small"
                  label={t('cabas.preview.purchasePrice')}
                  value={marginPrices[part.key] ?? ''}
                  onChange={(value) => onMarginPriceChange?.(part.key, value)}
                  disabled={!onMarginPriceChange || !preview.can_import}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                  inputProps={{ 'data-testid': `cabas-margin-price-${part.key}` }}
                />
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, flexWrap: 'wrap', mb: 1 }}>
        {totals.cabas_total_excl_vat != null && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {t('cabas.preview.cabasTotal')}
            </Typography>
            <Typography variant="body2" sx={numFont}>
              {formatPriceWithCurrency(totals.cabas_total_excl_vat)}
            </Typography>
          </Box>
        )}
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            {t('cabas.preview.totalExclVat')}
          </Typography>
          <Typography variant="body2" fontWeight={500} sx={numFont} data-testid="cabas-total-excl">
            {formatPriceWithCurrency(totals.lines_total)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            {t('cabas.preview.totalInclVat')}
          </Typography>
          <Typography variant="body1" fontWeight={700} sx={numFont}>
            {formatPriceWithCurrency(totals.lines_total_incl_vat)}
          </Typography>
        </Box>
      </Box>

      {preview.note && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setNotesOpen((open) => !open)}
            endIcon={
              <ExpandMoreIcon sx={{ transform: notesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            }
            sx={{ textTransform: 'none', px: 0.5 }}
            data-testid="cabas-preview-notes-toggle"
          >
            {t('cabas.preview.notes')}
          </Button>
          <Collapse in={notesOpen}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere', mt: 0.5 }}
              data-testid="cabas-preview-notes"
            >
              {preview.note}
            </Typography>
          </Collapse>
        </Box>
      )}

      {infos.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {infos.map((issue) => (
            <Typography key={issue.code + issue.message} variant="caption" color="text.secondary" component="div">
              {issue.message}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
